import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { getTenantId } from '../ApSetup/setupUtils';
import {
  fetchWorkforceBusinessUnits,
  formatBusinessUnitLabel,
  resolveWorkforceScope,
} from './workforceBusinessUnitUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const formatPropertyLabel = formatBusinessUnitLabel;

const defaultPropertyForm = {
  employeePropertyId: '',
  propertyId: '',
  isPrimary: 'YES',
  status: 'ACTIVE',
};

const defaultRolePositionForm = {
  employeeRolePositionId: '',
  rolePositionId: '',
  isPrimary: 'YES',
  assignmentType: 'PRIMARY',
  status: 'ACTIVE',
};

const assignmentTypeForIsPrimary = (isPrimary) => (isPrimary === 'YES' ? 'PRIMARY' : 'SECONDARY');

const defaultSkillCertForm = {
  employeeSkillCertificationId: '',
  recordType: 'SKILL',
  name: '',
  code: '',
  proficiencyLevel: 'INTERMEDIATE',
  issuingAuthority: '',
  certificationNumber: '',
  issuedDate: '',
  expiryDate: '',
  yearsOfExperience: '',
  isPrimary: 'NO',
  isMandatory: 'YES',
  status: 'ACTIVE',
  documentUrl: '',
  notes: '',
};

const defaultLeaveForm = {
  employeeLeaveId: '',
  propertyId: '',
  leaveDate: '',
  leaveEndDate: '',
  leaveReason: '',
};

/** Inclusive list of yyyy-MM-dd strings from start to end (UTC calendar days). Returns null if end < start. */
const enumerateLeaveDatesInclusive = (startYmd, endYmd) => {
  if (!startYmd || !endYmd) return null;
  const [sy, sm, sd] = startYmd.split('-').map(Number);
  const [ey, em, ed] = endYmd.split('-').map(Number);
  if ([sy, sm, sd, ey, em, ed].some((n) => !Number.isFinite(n))) return null;
  const start = Date.UTC(sy, sm - 1, sd);
  const end = Date.UTC(ey, em - 1, ed);
  if (end < start) return null;
  const out = [];
  for (let t = start; t <= end; t += 86400000) {
    const d = new Date(t);
    out.push(
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
    );
  }
  return out;
};

const AVAILABILITY_DAY_KEYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const LEGACY_WEEKDAY_TO_KEY = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
};

const mapApiEmployeePropertyToRow = (item, propertyLookup) => ({
  id: item.employeePropertyId,
  employeePropertyId: item.employeePropertyId,
  tenantId: item.tenantId || '',
  employeeId: item.employeeId || '',
  propertyId: item.propertyId || '',
  propertyName: propertyLookup[item.propertyId] || item.propertyId || '',
  isPrimary: item.isPrimary === true ? 'YES' : 'NO',
  status: item.status || '',
  createdAt: item.createdAt || '',
  updatedAt: item.updatedAt || '',
  createdBy: item.createdBy || '',
  updatedBy: item.updatedBy || '',
});

const mapApiEmployeeRolePositionToRow = (item, roleLookup) => ({
  id: item.employeeRolePositionId,
  employeeRolePositionId: item.employeeRolePositionId,
  tenantId: item.tenantId || '',
  employeeId: item.employeeId || '',
  rolePositionId: item.rolePositionId || '',
  rolePositionName: roleLookup[item.rolePositionId] || item.rolePositionId || '',
  isPrimary: item.isPrimary === true ? 'YES' : 'NO',
  assignmentType: item.assignmentType || '',
  status: item.status || '',
  createdAt: item.createdAt || '',
  updatedAt: item.updatedAt || '',
  createdBy: item.createdBy || '',
  updatedBy: item.updatedBy || '',
});

const toDateInputValue = (value) => {
  if (value == null || value === '') return '';
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const mapApiEmployeeSkillCertToRow = (item) => ({
  id: item.employeeSkillCertificationId,
  employeeSkillCertificationId: item.employeeSkillCertificationId,
  tenantId: item.tenantId || '',
  employeeId: item.employeeId || '',
  recordType: item.recordType || '',
  name: item.name || '',
  code: item.code || '',
  proficiencyLevel: item.proficiencyLevel || '',
  issuingAuthority: item.issuingAuthority || '',
  certificationNumber: item.certificationNumber || '',
  issuedDate: toDateInputValue(item.issuedDate),
  expiryDate: toDateInputValue(item.expiryDate),
  yearsOfExperience: item.yearsOfExperience != null ? item.yearsOfExperience : '',
  isPrimary: item.isPrimary === true ? 'YES' : 'NO',
  isMandatory: item.isMandatory === true ? 'YES' : 'NO',
  status: item.status || '',
  documentUrl: item.documentUrl || '',
  notes: item.notes || '',
  createdAt: item.createdAt || '',
  updatedAt: item.updatedAt || '',
  createdBy: item.createdBy || '',
  updatedBy: item.updatedBy || '',
});

const mapApiEmployeeLeaveToRow = (item, propertyLabel) => {
  const leaveFromDate = toDateInputValue(item.leaveFromDate ?? item.leaveDate);
  const leaveToDate = toDateInputValue(item.leaveToDate ?? item.leaveFromDate ?? item.leaveDate);
  return {
    id: item.employeeLeaveId,
    employeeLeaveId: item.employeeLeaveId,
    tenantId: item.tenantId || '',
    employeeId: item.employeeId || '',
    propertyId: item.propertyId || '',
    propertyLabel: propertyLabel || item.propertyId || '',
    leaveFromDate,
    leaveToDate: leaveToDate || leaveFromDate,
    leaveReason: item.leaveReason || '',
    createdAt: item.createdAt || '',
    updatedAt: item.updatedAt || '',
    createdBy: item.createdBy || '',
    updatedBy: item.updatedBy || '',
  };
};

const EmployeeDrawer = ({
  open,
  onClose,
  selectedEmployee,
  selectedBusinessUnit,
  onAvailabilitySaved,
  onLeaveChanged,
}) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const username = localStorage.getItem('username') || '';
  const userId = localStorage.getItem('user_id') || '';
  const token = localStorage.getItem('token') || '';
  const employeeId = selectedEmployee?.employee_uuid || selectedEmployee?.id || '';
  const employeePropertyId = selectedEmployee?.primary_property_id || '';
  const { propertyId: drawerPropertyId, businessUnitName: selectedBusinessUnitName } =
    resolveWorkforceScope(selectedBusinessUnit);
  const employeeName =
    selectedEmployee?.full_name ||
    [selectedEmployee?.first_name, selectedEmployee?.last_name].filter(Boolean).join(' ') ||
    selectedEmployee?.employee_code ||
    '';

  const [expanded, setExpanded] = useState('employeeProperties');
  const [employeeProperties, setEmployeeProperties] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingEmployeeProperties, setLoadingEmployeeProperties] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [submitting, setSubmitting] = useState(false);
  const [propertyForm, setPropertyForm] = useState(defaultPropertyForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [employeeRolePositions, setEmployeeRolePositions] = useState([]);
  const [rolePositions, setRolePositions] = useState([]);
  const [loadingEmployeeRolePositions, setLoadingEmployeeRolePositions] = useState(false);
  const [loadingRolePositions, setLoadingRolePositions] = useState(false);
  const [roleSelectionModel, setRoleSelectionModel] = useState([]);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [roleDialogMode, setRoleDialogMode] = useState('add');
  const [submittingRole, setSubmittingRole] = useState(false);
  const [roleForm, setRoleForm] = useState(defaultRolePositionForm);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [deleteRoleLoading, setDeleteRoleLoading] = useState(false);
  const [rowPendingRoleDelete, setRowPendingRoleDelete] = useState(null);
  const [skillCertifications, setSkillCertifications] = useState([]);
  const [loadingSkillCertifications, setLoadingSkillCertifications] = useState(false);
  const [skillCertSelectionModel, setSkillCertSelectionModel] = useState([]);
  const [openSkillCertDialog, setOpenSkillCertDialog] = useState(false);
  const [skillCertDialogMode, setSkillCertDialogMode] = useState('add');
  const [submittingSkillCert, setSubmittingSkillCert] = useState(false);
  const [skillCertForm, setSkillCertForm] = useState(defaultSkillCertForm);
  const [deleteSkillCertDialogOpen, setDeleteSkillCertDialogOpen] = useState(false);
  const [deleteSkillCertLoading, setDeleteSkillCertLoading] = useState(false);
  const [rowPendingSkillCertDelete, setRowPendingSkillCertDelete] = useState(null);
  const [skillCertMasters, setSkillCertMasters] = useState([]);
  const [loadingSkillCertMasters, setLoadingSkillCertMasters] = useState(false);
  const [employeeLeaves, setEmployeeLeaves] = useState([]);
  const [loadingEmployeeLeaves, setLoadingEmployeeLeaves] = useState(false);
  const [leaveSelectionModel, setLeaveSelectionModel] = useState([]);
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [leaveDialogMode, setLeaveDialogMode] = useState('add');
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [leaveForm, setLeaveForm] = useState(defaultLeaveForm);
  const [deleteLeaveDialogOpen, setDeleteLeaveDialogOpen] = useState(false);
  const [deleteLeaveLoading, setDeleteLeaveLoading] = useState(false);
  const [rowPendingLeaveDelete, setRowPendingLeaveDelete] = useState(null);
  const [availabilityType, setAvailabilityType] = useState('AVAILABLE');
  const [preferredDayConfig, setPreferredDayConfig] = useState({});
  const [savingAvailability, setSavingAvailability] = useState(false);

  const selectedEmployeePropertyId = selectionModel[0];
  const selectedEmployeeRolePositionId = roleSelectionModel[0];
  const selectedEmployeeSkillCertificationId = skillCertSelectionModel[0];
  const selectedEmployeeLeaveId = leaveSelectionModel[0];

  useEffect(() => {
    if (!open || !selectedEmployee) return;
    const availability = selectedEmployee.availability || {};

    const normalizeSlot = (v) => ({
      start_time: v?.start_time != null ? String(v.start_time).slice(0, 5) : '',
      end_time: v?.end_time != null ? String(v.end_time).slice(0, 5) : '',
      total_hours: v?.total_hours != null && v.total_hours !== '' ? String(v.total_hours) : '',
    });

    if (availability.preferred_day && typeof availability.preferred_day === 'object') {
      const next = {};
      Object.entries(availability.preferred_day).forEach(([k, v]) => {
        next[k] = normalizeSlot(v);
      });
      setAvailabilityType('PREFERRED_DAY');
      setPreferredDayConfig(next);
      return;
    }

    if (Array.isArray(availability.available) || Object.prototype.hasOwnProperty.call(availability, 'available')) {
      setAvailabilityType('AVAILABLE');
      setPreferredDayConfig({});
      return;
    }

    const hasLegacyPreferred = Object.prototype.hasOwnProperty.call(availability, 'PREFERRED_DAY');
    const hasLegacyAvailable = Object.prototype.hasOwnProperty.call(availability, 'AVAILABLE');

    if (hasLegacyPreferred && Array.isArray(availability.PREFERRED_DAY)) {
      const next = {};
      availability.PREFERRED_DAY.forEach((d) => {
        const k = LEGACY_WEEKDAY_TO_KEY[d] || String(d).toLowerCase();
        if (AVAILABILITY_DAY_KEYS.some((x) => x.key === k)) {
          next[k] = { start_time: '', end_time: '', total_hours: '' };
        }
      });
      setAvailabilityType('PREFERRED_DAY');
      setPreferredDayConfig(next);
      return;
    }

    if (hasLegacyAvailable) {
      setAvailabilityType('AVAILABLE');
      setPreferredDayConfig({});
      return;
    }

    setAvailabilityType('AVAILABLE');
    setPreferredDayConfig({});
  }, [open, selectedEmployee]);

  const propertyNameLookup = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.propertyId, formatPropertyLabel(p)])),
    [properties]
  );

  const employeePropertyColumns = useMemo(
    () => [
      { field: 'propertyName', headerName: 'Business Unit', flex: 1, minWidth: 220, resizable: true },
      { field: 'isPrimary', headerName: 'Is Primary', width: 120, resizable: true },
      { field: 'status', headerName: 'Status', width: 120, resizable: true },
    //   { field: 'createdBy', headerName: 'Created By', width: 170, resizable: true },
    //   { field: 'updatedBy', headerName: 'Updated By', width: 170, resizable: true },
    ],
    []
  );

  const rolePositionNameLookup = useMemo(
    () =>
      Object.fromEntries(
        rolePositions.map((r) => [r.rolePositionId, r.roleName || r.roleCode || r.rolePositionId])
      ),
    [rolePositions]
  );

  const employeeRolePositionColumns = useMemo(
    () => [
      { field: 'rolePositionName', headerName: 'Position', flex: 1, minWidth: 220, resizable: true },
      { field: 'isPrimary', headerName: 'Is Primary', width: 120, resizable: true },
      { field: 'assignmentType', headerName: 'Assignment Type', width: 150, resizable: true },
      { field: 'status', headerName: 'Status', width: 120, resizable: true },
    //   { field: 'createdBy', headerName: 'Created By', width: 170, resizable: true },
    //   { field: 'updatedBy', headerName: 'Updated By', width: 170, resizable: true },
    ],
    []
  );

  const skillCertificationColumns = useMemo(
    () => [
      { field: 'recordType', headerName: 'Type', width: 110, resizable: true },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140, resizable: true },
      { field: 'code', headerName: 'Code', width: 100, resizable: true },
      { field: 'proficiencyLevel', headerName: 'Proficiency', width: 130, resizable: true },
      { field: 'expiryDate', headerName: 'Expiry', width: 120, resizable: true },
      { field: 'isMandatory', headerName: 'Mandatory', width: 100, resizable: true },
      { field: 'status', headerName: 'Status', width: 100, resizable: true },
    ],
    []
  );

  const leaveColumns = useMemo(
    () => [
      { field: 'leaveFromDate', headerName: 'Leave from date', width: 130, resizable: true },
      { field: 'leaveToDate', headerName: 'Leave to date', width: 130, resizable: true },
      { field: 'leaveReason', headerName: 'Reason', flex: 1, minWidth: 200, resizable: true },
    ],
    []
  );

  const fetchProperties = async () => {
    if (!tenantId) {
      setProperties([]);
      return;
    }
    try {
      setLoadingProperties(true);
      const units = await fetchWorkforceBusinessUnits(tenantId);
      setProperties(units);
    } catch (error) {
      console.error('Error fetching business units for employee drawer:', error);
      setProperties([]);
      showError(error.message || 'Failed to fetch business units');
    } finally {
      setLoadingProperties(false);
    }
  };

  const fetchEmployeeProperties = async () => {
    if (!tenantId || !employeeId) {
      setEmployeeProperties([]);
      return;
    }
    try {
      setLoadingEmployeeProperties(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-properties/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&employee_id=${encodeURIComponent(employeeId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch employee properties: ${response.status}`);
      }
      const data = await response.json();
      const rows = Array.isArray(data)
        ? data.map((item) => mapApiEmployeePropertyToRow(item, propertyNameLookup))
        : [];
      setEmployeeProperties(rows);
    } catch (error) {
      console.error('Error fetching employee properties:', error);
      setEmployeeProperties([]);
      showError(error.message || 'Failed to fetch employee properties');
    } finally {
      setLoadingEmployeeProperties(false);
    }
  };

  const fetchRolePositions = async () => {
    if (!tenantId) {
      setRolePositions([]);
      return;
    }
    try {
      setLoadingRolePositions(true);
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`;
      if (employeePropertyId) {
        apiUrl += `&property_id=${encodeURIComponent(employeePropertyId)}`;
      }
      const response = await fetch(
        apiUrl,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch positions: ${response.status}`);
      }
      const data = await response.json();
      setRolePositions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching role positions for employee drawer:', error);
      setRolePositions([]);
      showError(error.message || 'Failed to fetch positions');
    } finally {
      setLoadingRolePositions(false);
    }
  };

  const fetchEmployeeRolePositions = async () => {
    if (!tenantId || !employeeId) {
      setEmployeeRolePositions([]);
      return;
    }
    try {
      setLoadingEmployeeRolePositions(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&employee_id=${encodeURIComponent(employeeId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch employee positions: ${response.status}`);
      }
      const data = await response.json();
      const rows = Array.isArray(data)
        ? data.map((item) => mapApiEmployeeRolePositionToRow(item, rolePositionNameLookup))
        : [];
      setEmployeeRolePositions(rows);
    } catch (error) {
      console.error('Error fetching employee role positions:', error);
      setEmployeeRolePositions([]);
      showError(error.message || 'Failed to fetch employee positions');
    } finally {
      setLoadingEmployeeRolePositions(false);
    }
  };

  const fetchEmployeeSkillCertifications = async () => {
    if (!tenantId || !employeeId) {
      setSkillCertifications([]);
      return;
    }
    try {
      setLoadingSkillCertifications(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-skill-certifications/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&employee_id=${encodeURIComponent(employeeId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch skill certifications: ${response.status}`);
      }
      const data = await response.json();
      const rows = Array.isArray(data) ? data.map((item) => mapApiEmployeeSkillCertToRow(item)) : [];
      setSkillCertifications(rows);
    } catch (error) {
      console.error('Error fetching employee skill certifications:', error);
      setSkillCertifications([]);
      showError(error.message || 'Failed to fetch skill certifications');
    } finally {
      setLoadingSkillCertifications(false);
    }
  };

  const fetchEmployeeLeaves = async () => {
    if (!tenantId || !employeeId || !drawerPropertyId) {
      setEmployeeLeaves([]);
      return;
    }
    try {
      setLoadingEmployeeLeaves(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-leaves/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&employee_id=${encodeURIComponent(employeeId)}&property_id=${encodeURIComponent(drawerPropertyId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch employee leaves: ${response.status}`);
      }
      const data = await response.json();
      const label = selectedBusinessUnitName || drawerPropertyId;
      const rows = Array.isArray(data)
        ? data.map((item) => mapApiEmployeeLeaveToRow(item, label))
        : [];
      setEmployeeLeaves(rows);
    } catch (error) {
      console.error('Error fetching employee leaves:', error);
      setEmployeeLeaves([]);
      showError(error.message || 'Failed to fetch employee leaves');
    } finally {
      setLoadingEmployeeLeaves(false);
    }
  };

  const fetchWorkforceSkillCertMasters = async (recordType, signal) => {
    const rt = recordType || 'SKILL';
    try {
      setLoadingSkillCertMasters(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workforce-skill-certificate-masters/by-record-type?record_type=${encodeURIComponent(rt)}`,
        {
          method: 'GET',
          signal,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch skill/certificate masters: ${response.status}`);
      }
      const data = await response.json();
      setSkillCertMasters(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching workforce skill certificate masters:', error);
      setSkillCertMasters([]);
      showError(error.message || 'Failed to fetch skill/certificate names');
    } finally {
      if (!signal?.aborted) {
        setLoadingSkillCertMasters(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchProperties();
    fetchRolePositions();
  }, [open, employeePropertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    fetchEmployeeProperties();
  }, [open, employeeId, propertyNameLookup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    fetchEmployeeRolePositions();
  }, [open, employeeId, rolePositionNameLookup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    fetchEmployeeSkillCertifications();
  }, [open, employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    fetchEmployeeLeaves();
  }, [open, employeeId, drawerPropertyId, selectedBusinessUnitName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openSkillCertDialog) return;
    const controller = new AbortController();
    fetchWorkforceSkillCertMasters(skillCertForm.recordType || 'SKILL', controller.signal);
    return () => {
      controller.abort();
      setLoadingSkillCertMasters(false);
    };
  }, [openSkillCertDialog, skillCertForm.recordType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenAddDialog = () => {
    if (!canAdd) return;
    setDialogMode('add');
    setPropertyForm(defaultPropertyForm);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = () => {
    if (!canEdit || !selectedEmployeePropertyId) return;
    const selected = employeeProperties.find((row) => row.id === selectedEmployeePropertyId);
    if (!selected) return;
    setDialogMode('edit');
    setPropertyForm({
      employeePropertyId: selected.employeePropertyId,
      propertyId: selected.propertyId,
      isPrimary: selected.isPrimary,
      status: selected.status || 'ACTIVE',
    });
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = () => {
    if (!canDelete || !selectedEmployeePropertyId) return;
    const selected = employeeProperties.find((row) => row.id === selectedEmployeePropertyId);
    if (!selected) return;
    setRowPendingDelete(selected);
    setDeleteDialogOpen(true);
  };

  const handleSaveEmployeeProperty = async (event) => {
    event.preventDefault();
    if (!propertyForm.propertyId) {
      showError('Business unit is required');
      return;
    }
    if (!tenantId || !employeeId) {
      showError('Missing tenant or employee context');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        tenantId,
        employeeId,
        propertyId: propertyForm.propertyId,
        isPrimary: propertyForm.isPrimary === 'YES',
        status: propertyForm.status || 'ACTIVE',
        createdBy: username,
        updatedBy: username,
      };
      const isEdit = dialogMode === 'edit' && propertyForm.employeePropertyId;
      const response = await fetch(
        isEdit
          ? `${process.env.REACT_APP_BACKEND_URL}conductor/employee-properties/${propertyForm.employeePropertyId}`
          : `${process.env.REACT_APP_BACKEND_URL}conductor/employee-properties`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save employee property: ${response.status}`);
      }
      setOpenDialog(false);
      showSuccess(isEdit ? 'Employee business unit updated successfully' : 'Employee business unit added successfully');
      await fetchEmployeeProperties();
    } catch (error) {
      console.error('Error saving employee property:', error);
      showError(error.message || 'Failed to save employee property');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    const employeePropertyId = rowPendingDelete?.employeePropertyId;
    if (!employeePropertyId) return;
    try {
      setDeleteLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-properties/${employeePropertyId}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete employee property: ${response.status}`);
      }
      setDeleteDialogOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      showSuccess('Employee business unit removed successfully');
      await fetchEmployeeProperties();
    } catch (error) {
      console.error('Error deleting employee property:', error);
      showError(error.message || 'Failed to delete employee property');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenAddRoleDialog = () => {
    if (!canAdd) return;
    setRoleDialogMode('add');
    setRoleForm(defaultRolePositionForm);
    setOpenRoleDialog(true);
  };

  const handleOpenEditRoleDialog = () => {
    if (!canEdit || !selectedEmployeeRolePositionId) return;
    const selected = employeeRolePositions.find((row) => row.id === selectedEmployeeRolePositionId);
    if (!selected) return;
    setRoleDialogMode('edit');
    const isPrimary = selected.isPrimary || 'NO';
    setRoleForm({
      employeeRolePositionId: selected.employeeRolePositionId,
      rolePositionId: selected.rolePositionId,
      isPrimary,
      assignmentType: selected.assignmentType || assignmentTypeForIsPrimary(isPrimary),
      status: selected.status || 'ACTIVE',
    });
    setOpenRoleDialog(true);
  };

  const handleOpenDeleteRoleDialog = () => {
    if (!canDelete || !selectedEmployeeRolePositionId) return;
    const selected = employeeRolePositions.find((row) => row.id === selectedEmployeeRolePositionId);
    if (!selected) return;
    setRowPendingRoleDelete(selected);
    setDeleteRoleDialogOpen(true);
  };

  const handleSaveEmployeeRolePosition = async (event) => {
    event.preventDefault();
    if (!roleForm.rolePositionId) {
      showError('Position is required');
      return;
    }
    if (!tenantId || !employeeId) {
      showError('Missing tenant or employee context');
      return;
    }

    try {
      setSubmittingRole(true);
      const payload = {
        tenantId,
        employeeId,
        rolePositionId: roleForm.rolePositionId,
        isPrimary: roleForm.isPrimary === 'YES',
        assignmentType: roleForm.assignmentType || 'PRIMARY',
        status: roleForm.status || 'ACTIVE',
        createdBy: username,
        updatedBy: username,
      };
      const isEdit = roleDialogMode === 'edit' && roleForm.employeeRolePositionId;
      const response = await fetch(
        isEdit
          ? `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/${roleForm.employeeRolePositionId}`
          : `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save employee position: ${response.status}`);
      }
      setOpenRoleDialog(false);
      showSuccess(isEdit ? 'Employee position updated successfully' : 'Employee position added successfully');
      await fetchEmployeeRolePositions();
    } catch (error) {
      console.error('Error saving employee role position:', error);
      showError(error.message || 'Failed to save employee position');
    } finally {
      setSubmittingRole(false);
    }
  };

  const handleConfirmDeleteRole = async () => {
    const employeeRolePositionId = rowPendingRoleDelete?.employeeRolePositionId;
    if (!employeeRolePositionId) return;
    try {
      setDeleteRoleLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/${employeeRolePositionId}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete employee position: ${response.status}`);
      }
      setDeleteRoleDialogOpen(false);
      setRowPendingRoleDelete(null);
      setRoleSelectionModel([]);
      showSuccess('Employee position deleted successfully');
      await fetchEmployeeRolePositions();
    } catch (error) {
      console.error('Error deleting employee role position:', error);
      showError(error.message || 'Failed to delete employee position');
    } finally {
      setDeleteRoleLoading(false);
    }
  };

  const handleOpenAddSkillCertDialog = () => {
    if (!canAdd) return;
    setSkillCertDialogMode('add');
    setSkillCertForm(defaultSkillCertForm);
    setOpenSkillCertDialog(true);
  };

  const handleOpenEditSkillCertDialog = () => {
    if (!canEdit || !selectedEmployeeSkillCertificationId) return;
    const selected = skillCertifications.find((row) => row.id === selectedEmployeeSkillCertificationId);
    if (!selected) return;
    setSkillCertDialogMode('edit');
    setSkillCertForm({
      employeeSkillCertificationId: selected.employeeSkillCertificationId,
      recordType: selected.recordType || 'SKILL',
      name: selected.name || '',
      code: selected.code || '',
      proficiencyLevel: selected.proficiencyLevel || 'INTERMEDIATE',
      issuingAuthority: selected.issuingAuthority || '',
      certificationNumber: selected.certificationNumber || '',
      issuedDate: selected.issuedDate || '',
      expiryDate: selected.expiryDate || '',
      yearsOfExperience: selected.yearsOfExperience === '' || selected.yearsOfExperience == null ? '' : String(selected.yearsOfExperience),
      isPrimary: selected.isPrimary || 'NO',
      isMandatory: selected.isMandatory || 'YES',
      status: selected.status || 'ACTIVE',
      documentUrl: selected.documentUrl || '',
      notes: selected.notes || '',
    });
    setOpenSkillCertDialog(true);
  };

  const handleOpenDeleteSkillCertDialog = () => {
    if (!canDelete || !selectedEmployeeSkillCertificationId) return;
    const selected = skillCertifications.find((row) => row.id === selectedEmployeeSkillCertificationId);
    if (!selected) return;
    setRowPendingSkillCertDelete(selected);
    setDeleteSkillCertDialogOpen(true);
  };

  const buildSkillCertPayload = () => {
    const years =
      skillCertForm.yearsOfExperience === '' || skillCertForm.yearsOfExperience == null
        ? null
        : Number(skillCertForm.yearsOfExperience);
    return {
      tenantId,
      employeeId,
      recordType: skillCertForm.recordType || 'SKILL',
      name: skillCertForm.name.trim(),
      code: skillCertForm.code.trim() || null,
      proficiencyLevel: skillCertForm.proficiencyLevel || null,
      issuingAuthority: skillCertForm.issuingAuthority.trim() || null,
      certificationNumber: skillCertForm.certificationNumber.trim() || null,
      issuedDate: skillCertForm.issuedDate || null,
      expiryDate: skillCertForm.expiryDate || null,
      yearsOfExperience: Number.isFinite(years) ? years : null,
      isPrimary: skillCertForm.isPrimary === 'YES',
      isMandatory: skillCertForm.isMandatory === 'YES',
      status: skillCertForm.status || 'ACTIVE',
      documentUrl: skillCertForm.documentUrl.trim() || null,
      notes: skillCertForm.notes.trim() || null,
      createdBy: userId,
      updatedBy: userId,
    };
  };

  const handleSaveSkillCertification = async (event) => {
    event.preventDefault();
    if (!skillCertForm.name.trim()) {
      showError('Name is required');
      return;
    }
    if (!tenantId || !employeeId) {
      showError('Missing tenant or employee context');
      return;
    }

    try {
      setSubmittingSkillCert(true);
      const payload = buildSkillCertPayload();
      const isEdit = skillCertDialogMode === 'edit' && skillCertForm.employeeSkillCertificationId;
      const response = await fetch(
        isEdit
          ? `${process.env.REACT_APP_BACKEND_URL}conductor/employee-skill-certifications/${skillCertForm.employeeSkillCertificationId}`
          : `${process.env.REACT_APP_BACKEND_URL}conductor/employee-skill-certifications`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save skill certification: ${response.status}`);
      }
      setOpenSkillCertDialog(false);
      showSuccess(isEdit ? 'Skill / certification updated successfully' : 'Skill / certification added successfully');
      await fetchEmployeeSkillCertifications();
    } catch (error) {
      console.error('Error saving skill certification:', error);
      showError(error.message || 'Failed to save skill certification');
    } finally {
      setSubmittingSkillCert(false);
    }
  };

  const handleConfirmDeleteSkillCert = async () => {
    const id = rowPendingSkillCertDelete?.employeeSkillCertificationId;
    if (!id) return;
    try {
      setDeleteSkillCertLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employee-skill-certifications/${id}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete skill certification: ${response.status}`);
      }
      setDeleteSkillCertDialogOpen(false);
      setRowPendingSkillCertDelete(null);
      setSkillCertSelectionModel([]);
      showSuccess('Skill / certification deleted successfully');
      await fetchEmployeeSkillCertifications();
    } catch (error) {
      console.error('Error deleting skill certification:', error);
      showError(error.message || 'Failed to delete skill certification');
    } finally {
      setDeleteSkillCertLoading(false);
    }
  };

  const handleOpenAddLeaveDialog = () => {
    if (!canAdd) return;
    if (!drawerPropertyId) {
      showError('Select a business unit in Workforce Operations before adding leave');
      return;
    }
    setLeaveDialogMode('add');
    setLeaveForm({ ...defaultLeaveForm, propertyId: drawerPropertyId });
    setOpenLeaveDialog(true);
  };

  const handleOpenEditLeaveDialog = () => {
    if (!canEdit || !selectedEmployeeLeaveId) return;
    const selected = employeeLeaves.find((row) => row.id === selectedEmployeeLeaveId);
    if (!selected) return;
    setLeaveDialogMode('edit');
    setLeaveForm({
      employeeLeaveId: selected.employeeLeaveId,
      propertyId: selected.propertyId || drawerPropertyId,
      leaveDate: selected.leaveFromDate || '',
      leaveEndDate: selected.leaveToDate || selected.leaveFromDate || '',
      leaveReason: selected.leaveReason || '',
    });
    setOpenLeaveDialog(true);
  };

  const handleOpenDeleteLeaveDialog = () => {
    if (!canDelete || !selectedEmployeeLeaveId) return;
    const selected = employeeLeaves.find((row) => row.id === selectedEmployeeLeaveId);
    if (!selected) return;
    setRowPendingLeaveDelete(selected);
    setDeleteLeaveDialogOpen(true);
  };

  const handleSaveEmployeeLeave = async (event) => {
    event.preventDefault();
    if (!drawerPropertyId) {
      showError('Business unit is required');
      return;
    }
    if (!leaveForm.leaveDate) {
      showError('Leave start date is required');
      return;
    }
    if (!leaveForm.leaveReason.trim()) {
      showError('Leave reason is required');
      return;
    }
    if (!tenantId || !employeeId) {
      showError('Missing tenant or employee context');
      return;
    }

    const isEdit = leaveDialogMode === 'edit' && leaveForm.employeeLeaveId;
    const endDate = (leaveForm.leaveEndDate || leaveForm.leaveDate).trim() || leaveForm.leaveDate;
    const dates = enumerateLeaveDatesInclusive(leaveForm.leaveDate, endDate);
    if (!dates) {
      showError('Leave to date must be on or after leave from date');
      return;
    }
    if (dates.length > 366) {
      showError('Date range cannot exceed 366 days');
      return;
    }

    try {
      setSubmittingLeave(true);
      const basePayload = {
        tenantId,
        employeeId,
        propertyId: drawerPropertyId,
        leaveReason: leaveForm.leaveReason.trim(),
        createdBy: userId,
        updatedBy: userId,
      };

      if (isEdit) {
        const leaveToDate =
          (leaveForm.leaveEndDate || leaveForm.leaveDate).trim() || leaveForm.leaveDate;
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/employee-leaves/${leaveForm.employeeLeaveId}`,
          {
            method: 'PUT',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...basePayload,
              leaveFromDate: leaveForm.leaveDate,
              leaveToDate,
              leaveReason: leaveForm.leaveReason.trim(),
            }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Failed to save leave: ${response.status}`);
        }
        setOpenLeaveDialog(false);
        showSuccess('Leave updated successfully');
        await fetchEmployeeLeaves();
        onLeaveChanged?.();
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/employee-leaves`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId,
          employeeId,
          propertyId: drawerPropertyId,
          leaveFromDate: leaveForm.leaveDate,
          leaveToDate: endDate,
          leaveReason: leaveForm.leaveReason.trim(),
          createdBy: userId,
          updatedBy: userId,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to add leave: ${response.status}`);
      }
      setOpenLeaveDialog(false);
      showSuccess('Leave added successfully');
      await fetchEmployeeLeaves();
      onLeaveChanged?.();
    } catch (error) {
      console.error('Error saving employee leave:', error);
      showError(error.message || 'Failed to save leave');
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleConfirmDeleteLeave = async () => {
    const id = rowPendingLeaveDelete?.employeeLeaveId;
    if (!id) return;
    try {
      setDeleteLeaveLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/employee-leaves/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete leave: ${response.status}`);
      }
      setDeleteLeaveDialogOpen(false);
      setRowPendingLeaveDelete(null);
      setLeaveSelectionModel([]);
      showSuccess('Leave deleted successfully');
      await fetchEmployeeLeaves();
      onLeaveChanged?.();
    } catch (error) {
      console.error('Error deleting employee leave:', error);
      showError(error.message || 'Failed to delete leave');
    } finally {
      setDeleteLeaveLoading(false);
    }
  };

  const handleTogglePreferredDay = (dayKey) => {
    setPreferredDayConfig((prev) => {
      if (prev[dayKey]) {
        const { [dayKey]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dayKey]: { start_time: '', end_time: '', total_hours: '' } };
    });
  };

  const updatePreferredDaySlot = (dayKey, field, value) => {
    setPreferredDayConfig((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  const buildPreferredDayPayload = () => {
    const preferred_day = {};
    for (const { key } of AVAILABILITY_DAY_KEYS) {
      if (!preferredDayConfig[key]) continue;
      const slot = preferredDayConfig[key];
      const start = (slot.start_time || '').trim();
      const end = (slot.end_time || '').trim();
      if (!start || !end) {
        const dayLabel = AVAILABILITY_DAY_KEYS.find((x) => x.key === key)?.label || key;
        showError(`Enter start and end time for ${dayLabel}`);
        return null;
      }
      const th = parseFloat(String(slot.total_hours).replace(',', '.'));
      preferred_day[key] = {
        start_time: start.length === 5 ? start : start.slice(0, 5),
        end_time: end.length === 5 ? end : end.slice(0, 5),
        total_hours: Number.isFinite(th) ? th : 0,
      };
    }
    if (Object.keys(preferred_day).length === 0) {
      showError('Select at least one day and enter times');
      return null;
    }
    return preferred_day;
  };

  const handleSaveAvailability = async () => {
    if (!tenantId || !employeeId) {
      showError('Missing tenant or employee context');
      return;
    }

    let body;
    if (availabilityType === 'AVAILABLE') {
      body = { available: [] };
    } else {
      const preferred_day = buildPreferredDayPayload();
      if (!preferred_day) return;
      body = { preferred_day };
    }

    try {
      setSavingAvailability(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employees/${employeeId}/availability`,
        {
          method: 'PATCH',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to update availability: ${response.status}`);
      }

      showSuccess('Availability updated successfully');
      if (onAvailabilitySaved) {
        onAvailabilitySaved();
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      showError(error.message || 'Failed to update availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 700,
            maxWidth: '100%',
            bgcolor: ht.drawerBg,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={harmonateDrawerHeaderFlushSx}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
              Employee Details
            </Typography>
            <Typography variant="body2" sx={{ color: ht.white }}>
              {selectedBusinessUnitName || employeePropertyId || ''}
            </Typography>
            <Typography variant="body2" sx={{ color: ht.white }}>
              {employeeName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ ...harmonateDrawerBodySx, overflow: 'auto' }}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        <Accordion
          expanded={expanded === 'employeeProperties'}
          onChange={() => setExpanded(expanded === 'employeeProperties' ? '' : 'employeeProperties')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Employee Business Units</Typography>
            <Tooltip title="Edit Employee Business Unit">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedEmployeePropertyId || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditDialog();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Employee Business Unit">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedEmployeePropertyId || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Employee Business Unit' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAddDialog();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
              <DataGridPro
                rows={employeeProperties}
                columns={employeePropertyColumns}
                columnResizeMode="onEnd"
                resizable
                getRowId={(row) => row.id}
                rowHeight={35}
                loading={loadingEmployeeProperties || loadingProperties}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(model) => setSelectionModel(model)}
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={100}
                autoPageSize={false}
                sx={{ ...harmonateDataGridSx, flex: 1 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'employeeRolePositions'}
          onChange={() => setExpanded(expanded === 'employeeRolePositions' ? '' : 'employeeRolePositions')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Position</Typography>
            <Tooltip title="Edit Employee Position">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedEmployeeRolePositionId || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditRoleDialog();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Employee Position">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedEmployeeRolePositionId || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteRoleDialog();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Employee Position' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAddRoleDialog();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
              <DataGridPro
                rows={employeeRolePositions}
                columns={employeeRolePositionColumns}
                columnResizeMode="onEnd"
                resizable
                getRowId={(row) => row.id}
                rowHeight={35}
                loading={loadingEmployeeRolePositions || loadingRolePositions}
                rowSelectionModel={roleSelectionModel}
                onRowSelectionModelChange={(model) => setRoleSelectionModel(model)}
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={100}
                autoPageSize={false}
                sx={{ ...harmonateDataGridSx, flex: 1 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'skillCertifications'}
          onChange={() => setExpanded(expanded === 'skillCertifications' ? '' : 'skillCertifications')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Skill / Certifications</Typography>
            <Tooltip title="Edit skill or certification">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedEmployeeSkillCertificationId || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditSkillCertDialog();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete skill or certification">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedEmployeeSkillCertificationId || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteSkillCertDialog();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add skill or certification' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAddSkillCertDialog();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
              <DataGridPro
                rows={skillCertifications}
                columns={skillCertificationColumns}
                columnResizeMode="onEnd"
                resizable
                getRowId={(row) => row.id}
                rowHeight={35}
                loading={loadingSkillCertifications}
                rowSelectionModel={skillCertSelectionModel}
                onRowSelectionModelChange={(model) => setSkillCertSelectionModel(model)}
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={100}
                autoPageSize={false}
                sx={{ ...harmonateDataGridSx, flex: 1 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'employeeLeaves'}
          onChange={() => setExpanded(expanded === 'employeeLeaves' ? '' : 'employeeLeaves')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Leave</Typography>
            <Tooltip title="Edit leave">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedEmployeeLeaveId || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditLeaveDialog();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete leave">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedEmployeeLeaveId || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteLeaveDialog();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={!canAdd ? 'No add permission' : drawerPropertyId ? 'Add leave' : 'Select a business unit in Workforce Operations'}>
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerAddIconSx}
                  disabled={!drawerPropertyId || !canAdd}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddLeaveDialog();
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
              <DataGridPro
                rows={employeeLeaves}
                columns={leaveColumns}
                columnResizeMode="onEnd"
                resizable
                getRowId={(row) => row.id}
                rowHeight={35}
                loading={loadingEmployeeLeaves}
                rowSelectionModel={leaveSelectionModel}
                onRowSelectionModelChange={(model) => setLeaveSelectionModel(model)}
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={100}
                autoPageSize={false}
                sx={{ ...harmonateDataGridSx, flex: 1 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'availability'}
          onChange={() => setExpanded(expanded === 'availability' ? '' : 'availability')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Availability</Typography>
          </AccordionSummary>
          <AccordionDetails sx={harmonateDrawerAccordionDetailsSx}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, ...harmonateFormFieldsSx }}>
              <RadioGroup
                value={availabilityType}
                onChange={(e) => {
                  const value = e.target.value;
                  setAvailabilityType(value);
                  if (value === 'AVAILABLE') {
                    setPreferredDayConfig({});
                  }
                }}
              >
                <FormControlLabel value="AVAILABLE" control={<Radio />} label="AVAILABLE" />
                <FormControlLabel value="PREFERRED_DAY" control={<Radio />} label="PREFERRED DAY" />
              </RadioGroup>

              {availabilityType === 'PREFERRED_DAY' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {AVAILABILITY_DAY_KEYS.map(({ key, label }) => (
                    <Box key={key} sx={{ border: `1px solid ${ht.line}`, borderRadius: ht.fieldRadius, p: 1.5, bgcolor: ht.white }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!preferredDayConfig[key]}
                            onChange={() => handleTogglePreferredDay(key)}
                          />
                        }
                        label={label}
                      />
                      {preferredDayConfig[key] && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pl: { xs: 0, sm: 4 }, mt: 1 }}>
                          <TextField
                            label="Start time"
                            type="time"
                            size="small"
                            value={preferredDayConfig[key].start_time}
                            onChange={(e) => updatePreferredDaySlot(key, 'start_time', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            sx={{ width: 140 }}
                          />
                          <TextField
                            label="End time"
                            type="time"
                            size="small"
                            value={preferredDayConfig[key].end_time}
                            onChange={(e) => updatePreferredDaySlot(key, 'end_time', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            sx={{ width: 140 }}
                          />
                          <TextField
                            label="Total hours"
                            size="small"
                            value={preferredDayConfig[key].total_hours}
                            onChange={(e) => updatePreferredDaySlot(key, 'total_hours', e.target.value)}
                            sx={{ width: 120 }}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveAvailability}
                  disabled={savingAvailability}
                  sx={harmonateProceedButtonSx}
                >
                  {savingAvailability ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        </Box>
        </Box>
      </Drawer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{dialogMode === 'edit' ? 'Edit Employee Business Unit' : 'Add Employee Business Unit'}</DialogTitle>
        <form onSubmit={handleSaveEmployeeProperty}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField label="Employee" size="small" value={employeeName} disabled />
            <Autocomplete
              size="small"
              options={properties}
              loading={loadingProperties}
              getOptionLabel={(option) => formatPropertyLabel(option)}
              isOptionEqualToValue={(a, b) => a?.propertyId === b?.propertyId}
              value={properties.find((p) => p.propertyId === propertyForm.propertyId) || null}
              onChange={(_, newValue) =>
                setPropertyForm((prev) => ({ ...prev, propertyId: newValue?.propertyId || '' }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Business Unit" required placeholder="Search business unit" />
              )}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                label="Is Primary"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                fullWidth
                value={propertyForm.isPrimary}
                onChange={(e) => setPropertyForm((prev) => ({ ...prev, isPrimary: e.target.value }))}
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
              <TextField
                label="Status"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                fullWidth
                value={propertyForm.status}
                onChange={(e) => setPropertyForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDialog(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? (dialogMode === 'edit' ? 'Updating...' : 'Saving...') : (dialogMode === 'edit' ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this employee business unit mapping?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{roleDialogMode === 'edit' ? 'Edit Employee Position' : 'Add Employee Position'}</DialogTitle>
        <form onSubmit={handleSaveEmployeeRolePosition}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField label="Employee" size="small" value={employeeName} disabled />
            <TextField
              label="Position"
              size="small"
              select
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              value={roleForm.rolePositionId}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, rolePositionId: e.target.value }))}
              required
            >
              <MenuItem value="">
                <em>Select position</em>
              </MenuItem>
              {rolePositions.map((r) => (
                <MenuItem key={r.rolePositionId} value={r.rolePositionId}>
                  {r.roleName || r.roleCode || r.rolePositionId}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                label="Is Primary"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                fullWidth
                value={roleForm.isPrimary}
                onChange={(e) => {
                  const isPrimary = e.target.value;
                  setRoleForm((prev) => ({
                    ...prev,
                    isPrimary,
                    assignmentType: assignmentTypeForIsPrimary(isPrimary),
                  }));
                }}
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
              <TextField
                label="Assignment Type"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                fullWidth
                value={roleForm.assignmentType}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, assignmentType: e.target.value }))}
              >
                <MenuItem value="PRIMARY">PRIMARY</MenuItem>
                <MenuItem value="SECONDARY">SECONDARY</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Status"
              size="small"
              select
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              value={roleForm.status}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenRoleDialog(false)} disabled={submittingRole} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submittingRole} sx={harmonateProceedButtonSx}>
              {submittingRole ? (roleDialogMode === 'edit' ? 'Updating...' : 'Saving...') : (roleDialogMode === 'edit' ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteRoleDialogOpen} onClose={() => !deleteRoleLoading && setDeleteRoleDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this employee position mapping?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteRoleDialogOpen(false)} disabled={deleteRoleLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteRole}
            variant="contained"
            disabled={deleteRoleLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteRoleLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSkillCertDialog} onClose={() => setOpenSkillCertDialog(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{skillCertDialogMode === 'edit' ? 'Edit Skill / Certification' : 'Add Skill / Certification'}</DialogTitle>
        <form onSubmit={handleSaveSkillCertification}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField label="Employee" size="small" value={employeeName} disabled />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Record type"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ flex: '1 1 200px', minWidth: 160 }}
                value={skillCertForm.recordType}
                onChange={(e) =>
                  setSkillCertForm((prev) => ({ ...prev, recordType: e.target.value, name: '' }))
                }
              >
                <MenuItem value="SKILL">SKILL</MenuItem>
                <MenuItem value="CERTIFICATION">CERTIFICATION</MenuItem>
              </TextField>
              <TextField
                label="Status"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ flex: '1 1 160px', minWidth: 140 }}
                value={skillCertForm.status}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Name"
              size="small"
              select
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              required
              fullWidth
              value={skillCertForm.name}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, name: e.target.value }))}
              disabled={loadingSkillCertMasters}
              helperText={loadingSkillCertMasters ? 'Loading names…' : undefined}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Select name</em>
              </MenuItem>
              {skillCertMasters.map((m) => (
                <MenuItem key={m.id} value={m.name}>
                  {m.name}
                </MenuItem>
              ))}
              {skillCertForm.name &&
                !skillCertMasters.some((m) => m.name === skillCertForm.name) && (
                  <MenuItem value={skillCertForm.name}>{skillCertForm.name}</MenuItem>
                )}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Code"
                size="small"
                sx={{ flex: '1 1 160px', minWidth: 120 }}
                value={skillCertForm.code}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, code: e.target.value }))}
              />
              <TextField
                label="Proficiency level"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ flex: '1 1 200px', minWidth: 160 }}
                value={skillCertForm.proficiencyLevel}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, proficiencyLevel: e.target.value }))}
              >
                <MenuItem value="BEGINNER">BEGINNER</MenuItem>
                <MenuItem value="INTERMEDIATE">INTERMEDIATE</MenuItem>
                <MenuItem value="ADVANCED">ADVANCED</MenuItem>
                <MenuItem value="EXPERT">EXPERT</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Issuing authority"
              size="small"
              fullWidth
              value={skillCertForm.issuingAuthority}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, issuingAuthority: e.target.value }))}
            />
            <TextField
              label="Certification number"
              size="small"
              fullWidth
              value={skillCertForm.certificationNumber}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, certificationNumber: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Issued date"
                type="date"
                size="small"
                sx={{ flex: '1 1 180px', minWidth: 160 }}
                value={skillCertForm.issuedDate}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, issuedDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Expiry date"
                type="date"
                size="small"
                sx={{ flex: '1 1 180px', minWidth: 160 }}
                value={skillCertForm.expiryDate}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, expiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="Years of experience"
              type="number"
              size="small"
              fullWidth
              value={skillCertForm.yearsOfExperience}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, yearsOfExperience: e.target.value }))}
              inputProps={{ step: 0.1, min: 0 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Is primary"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ flex: '1 1 160px', minWidth: 140 }}
                value={skillCertForm.isPrimary}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, isPrimary: e.target.value }))}
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
              <TextField
                label="Is mandatory"
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ flex: '1 1 160px', minWidth: 140 }}
                value={skillCertForm.isMandatory}
                onChange={(e) => setSkillCertForm((prev) => ({ ...prev, isMandatory: e.target.value }))}
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Document URL"
              size="small"
              fullWidth
              value={skillCertForm.documentUrl}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, documentUrl: e.target.value }))}
            />
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={skillCertForm.notes}
              onChange={(e) => setSkillCertForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenSkillCertDialog(false)} disabled={submittingSkillCert} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submittingSkillCert} sx={harmonateProceedButtonSx}>
              {submittingSkillCert ? (skillCertDialogMode === 'edit' ? 'Updating...' : 'Saving...') : (skillCertDialogMode === 'edit' ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteSkillCertDialogOpen} onClose={() => !deleteSkillCertLoading && setDeleteSkillCertDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this skill or certification record?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteSkillCertDialogOpen(false)} disabled={deleteSkillCertLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSkillCert}
            variant="contained"
            disabled={deleteSkillCertLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteSkillCertLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openLeaveDialog} onClose={() => setOpenLeaveDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{leaveDialogMode === 'edit' ? 'Edit Leave' : 'Add Leave'}</DialogTitle>
        <form onSubmit={handleSaveEmployeeLeave}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField label="Employee" size="small" value={employeeName} disabled />
            <TextField
              label="Business Unit"
              size="small"
              value={selectedBusinessUnitName || drawerPropertyId || ''}
              disabled
              fullWidth
            />
            <TextField
              label="Leave from date"
              type="date"
              size="small"
              required
              fullWidth
              value={leaveForm.leaveDate}
              onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Leave to date"
              type="date"
              size="small"
              required
              fullWidth
              value={leaveForm.leaveEndDate || leaveForm.leaveDate}
              onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveEndDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText={
                leaveDialogMode === 'add'
                  ? 'Use the same date as from date for a single day, or pick the last day of the range (inclusive).'
                  : 'Must be on or after leave from date.'
              }
            />
            <TextField
              label="Leave reason"
              size="small"
              required
              fullWidth
              multiline
              minRows={2}
              value={leaveForm.leaveReason}
              onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveReason: e.target.value }))}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenLeaveDialog(false)} disabled={submittingLeave} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submittingLeave} sx={harmonateProceedButtonSx}>
              {submittingLeave ? (leaveDialogMode === 'edit' ? 'Updating...' : 'Saving...') : (leaveDialogMode === 'edit' ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteLeaveDialogOpen} onClose={() => !deleteLeaveLoading && setDeleteLeaveDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>Are you sure you want to delete this leave record?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteLeaveDialogOpen(false)} disabled={deleteLeaveLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteLeave}
            variant="contained"
            disabled={deleteLeaveLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLeaveLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </>
  );
};

export default EmployeeDrawer;
